import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

// =============================================================================
// PageLoader — the branded full-page loading state.
//
// One loader for every "the page itself isn't ready yet" surface: the
// auth/session resolution screen (ProtectedRoute) and the Suspense
// fallback while a lazy route chunk fetches (App.tsx). Replaces the
// bare centered Spinner both used before.
//
// Design notes:
//
//   * Delayed fallback — the loader renders nothing visible for the
//     first 150ms. Instant loads (warm chunk cache, resolved session)
//     unmount it before the timer fires, so fast paths never flash a
//     loader frame. Only genuinely slow loads ever see it. The
//     container keeps its full size during the delay so nothing
//     shifts when the loader (or the content) arrives.
//
//   * Branded, not a spinner — the steering-wheel mark from the
//     favicon (redrawn in theme tokens) with a slow scale/opacity
//     "breathe", the MSMC PORTAL wordmark, and a thin indeterminate
//     progress sweep. The loading screen is the one place a loop is
//     allowed; the content that replaces it still enters ≤300ms.
//
//   * Reduced motion — the breathe + progress loops are gated with
//     `motion-safe:` (same pattern as the Skeleton shimmer), so those
//     users get a calm static brand lockup. The entrance fade
//     (animate-content-in) is neutralized by the global CSS block.
//
//   * Height matches the old fallback (min-h-[60vh]) so swapping the
//     Spinner for this is layout-neutral, and the boot loader in
//     index.html centers its mark in the same band of the viewport.
// =============================================================================

export function PageLoader({
  label = "Loading",
  className,
}: {
  /** Screen-reader announcement. Defaults to "Loading". */
  label?: string;
  className?: string;
}) {
  // The classic delayed-fallback gate: invisible for the first 150ms.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), 150);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <main
      className={cn("flex min-h-[60vh] items-center justify-center", className)}
    >
      {visible ? (
        <div
          role="status"
          aria-live="polite"
          className="flex animate-content-in flex-col items-center gap-4"
        >
          <BrandMark className="h-12 w-12 motion-safe:animate-breathe" />
          <p
            aria-hidden="true"
            className="text-[0.7rem] font-bold uppercase tracking-[0.3em] text-muted-foreground"
          >
            MSMC&nbsp;Portal
          </p>
          {/* Indeterminate progress hint: a third-width gradient bar
              sweeping a 10rem track. Pure transform — no width
              animation, no layout work. */}
          <div className="relative h-1 w-40 overflow-hidden rounded-full bg-foreground/10">
            <span className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-gradient-to-r from-primary to-[hsl(var(--accent-2))] motion-safe:animate-loader-bar" />
          </div>
          <span className="sr-only">{label}…</span>
        </div>
      ) : null}
    </main>
  );
}

/** The favicon's steering-wheel mark, redrawn against theme tokens so
 *  it adapts to light/dark instead of shipping the dark-tile colours.
 *  Ring takes the primary→violet brand gradient; hub + spokes use the
 *  current foreground. */
function BrandMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <defs>
        <linearGradient
          id="page-loader-ring"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" style={{ stopColor: "hsl(var(--primary))" }} />
          <stop offset="100%" style={{ stopColor: "hsl(var(--accent-2))" }} />
        </linearGradient>
      </defs>
      <circle
        cx="32"
        cy="32"
        r="17.5"
        stroke="url(#page-loader-ring)"
        strokeWidth="4.5"
      />
      <circle cx="32" cy="32" r="4.4" fill="currentColor" />
      <g stroke="currentColor" strokeLinecap="round" strokeWidth="2.7">
        <line x1="32" y1="14.8" x2="32" y2="20.3" />
        <line x1="32" y1="43.7" x2="32" y2="49.2" />
        <line x1="14.8" y1="32" x2="20.3" y2="32" />
        <line x1="43.7" y1="32" x2="49.2" y2="32" />
        <line x1="20.2" y1="20.2" x2="24.1" y2="24.1" />
        <line x1="39.9" y1="39.9" x2="43.8" y2="43.8" />
        <line x1="20.2" y1="43.8" x2="24.1" y2="39.9" />
        <line x1="39.9" y1="24.1" x2="43.8" y2="20.2" />
      </g>
    </svg>
  );
}
